import type { BlockNoteEditor } from "@blocknote/core";
import { COMMENT_THREAD_STYLE } from "./editorSchema";
import { findQuoteRange } from "./editorCommentQuote";
import {
  canUseEditorView,
  getCommentsExtension,
  type PendingCommentSelection,
} from "./editorComments";

const clampRange = (
  editor: BlockNoteEditor,
  selection: Pick<PendingCommentSelection, "from" | "to">
) => {
  const size = editor.prosemirrorState.doc.content.size;
  const from = Math.max(1, Math.min(selection.from, size));
  const to = Math.max(from, Math.min(selection.to, size));
  return { from, to };
};

export const applyCommentMarkToSelection = (
  editor: BlockNoteEditor,
  threadId: string,
  selection: Pick<PendingCommentSelection, "from" | "to">,
  options?: { stopPending?: boolean }
) => {
  if (!canUseEditorView(editor)) return;

  const schema = editor.prosemirrorState.schema;
  const commentMark = schema.marks.comment;
  const threadStyle = schema.marks[COMMENT_THREAD_STYLE];
  if (!commentMark && !threadStyle) return;

  const { from, to } = clampRange(editor, selection);

  try {
    editor.transact((tr) => {
      if (threadStyle) {
        tr.addMark(from, to, threadStyle.create({ stringValue: threadId }));
      }
      if (commentMark) {
        tr.addMark(
          from,
          to,
          commentMark.create({ orphan: false, threadId })
        );
      }
    });
  } catch {
    return;
  }

  if (options?.stopPending === false) return;
  try {
    getCommentsExtension(editor)?.stopPendingComment();
  } catch {
    return;
  }
};

export const syncPluginMarksFromPersistedThreads = (
  editor: BlockNoteEditor
) => {
  if (!canUseEditorView(editor)) return;

  const schema = editor.prosemirrorState.schema;
  const commentMark = schema.marks.comment;
  const threadStyle = schema.marks[COMMENT_THREAD_STYLE];
  if (!commentMark || !threadStyle) return;

  const ranges: Array<{ from: number; to: number; threadId: string }> = [];
  editor.prosemirrorState.doc.descendants((node, pos) => {
    node.marks.forEach((mark) => {
      if (mark.type !== threadStyle) return;
      const threadId = mark.attrs.stringValue as string | undefined;
      if (!threadId) return;
      ranges.push({ from: pos, to: pos + node.nodeSize, threadId });
    });
    return true;
  });

  if (!ranges.length) return;

  try {
    editor.transact((tr) => {
      ranges.forEach(({ from, to, threadId }) => {
        tr.addMark(
          from,
          to,
          commentMark.create({ orphan: false, threadId })
        );
      });
    });
  } catch {
    return;
  }
};

export const applyCommentMarkToQuote = (
  editor: BlockNoteEditor,
  threadId: string,
  quote: string,
  options?: { stopPending?: boolean }
): boolean => {
  const found = findQuoteRange(editor, quote, threadId);
  if (!found) return false;
  applyCommentMarkToSelection(editor, threadId, found, options);
  return true;
};

export const restoreAnchorsOnEditor = (
  editor: BlockNoteEditor,
  anchors: Array<{ threadId: string; quote: string }>,
  attempt = 0
) => {
  if (!canUseEditorView(editor)) {
    if (attempt >= 40) return;
    window.setTimeout(() => {
      restoreAnchorsOnEditor(editor, anchors, attempt + 1);
    }, 25);
    return;
  }

  syncPluginMarksFromPersistedThreads(editor);

  const missing = anchors.filter(
    (anchor) =>
      !applyCommentMarkToQuote(editor, anchor.threadId, anchor.quote, {
        stopPending: false,
      })
  );

  if (missing.length && attempt < 20) {
    window.setTimeout(() => {
      restoreAnchorsOnEditor(editor, missing, attempt + 1);
    }, 50);
  }
};
